<template>
  <div>
    <Header>
      勤務終了
      <template v-slot:right>
        <FeatherIcon
          icon="x"
          @click="$router.push({ name: 'ApplicationsList' })"
        />
      </template>
    </Header>

    <div class="py-5 px-3 text-center">
      <div v-if="hasAttendanceCorrection" class="caution text-left">
        <dt
          class="text-weight-500 text-red text-size-16 mb-1"
          data-testid="needAttendanceCorrection"
        >
          勤怠変更申請が必要です
        </dt>
        <div class="text-size-16">
          <p>
            勤怠変更申請が承認され次第、勤務実績に沿った給与がウォレットに反映されます
          </p>
        </div>
      </div>
      <div>
        <Balloon />
      </div>
      <p class="mt-4 text-weight-500" data-testid="message">
        お仕事お疲れ様でした！<br />
        事業所への評価を必ず行ってください
      </p>
      <AppButton
        v-if="hasAttendanceCorrection"
        block
        class="mt-4"
        variant="green"
        :to="{
          name: 'AttendanceCorrection',
          params: { applicationId }
        }"
      >
        勤怠変更申請を行う
      </AppButton>
      <AppButton
        v-else
        block
        class="mt-4"
        :to="{
          name: 'ApplicationEvaluation',
          params: { applicationId }
        }"
      >
        事業所評価へ
      </AppButton>

      <AppButtonOutline
        block
        class="mt-3"
        @click="$router.push({ name: 'ApplicationsList' })"
      >
        仕事管理に戻る
      </AppButtonOutline>

      <RecentOrdersWithRecommendText
        v-if="!hasAttendanceCorrection"
        class="mt-4"
        :currentOrder="order"
        :orders="orders"
        :applicableOrderCount="applicableOrderCount"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import AppButton from "@/components/atoms/button/AppButton.vue";
import AppButtonOutline from "@/components/atoms/button/AppButtonOutline.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import RecentOrdersWithRecommendText from "@/components/organisms/orders/RecentOrdersWithRecommendText.vue";
import { applicationService } from "@/app/services/ApplicationService";
import { inAppReviewService } from "@/app/services/InAppReviewService";
import { orderService } from "@/app/services/OrderService";
import { authStore } from "@/store/auth";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { Application } from "@/types/order/Application";
import { Order } from "@/types/order/Order";

@Component({
  components: {
    AppButton,
    AppButtonOutline,
    FeatherIcon,
    Header,
    RecentOrdersWithRecommendText
  }
})
export default class WorkFinished extends Vue {
  application: Application | null = null;

  orders: Order[] = [];
  applicableOrderCount = 0;

  get applicationId() {
    return this.$route.params.applicationId;
  }

  get hasAttendanceCorrection() {
    return !!this.application?.attendanceCorrection;
  }

  get order() {
    return this.application?.order;
  }

  async created() {
    this.$loading.show();
    try {
      await this.fetchApplication();

      // 勤怠変更申請がある場合は、応募可能案件の取得は行わない
      if (!this.hasAttendanceCorrection) {
        await this.fetchEachDayOrders();
      }
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async mounted() {
    await inAppReviewService.launchInAppReview("FINISH_WORK");
  }

  async fetchApplication() {
    this.application = await applicationService.fetchApplication(
      Number(this.applicationId)
    );
  }

  async fetchEachDayOrders() {
    if (!this.order) return;
    const { results, count } = await orderService.fetchOrdersEachDayWithCount({
      office: this.order.office.id,
      serviceName: authStore.workerService.serviceName,
      onlyHiring: true
    });
    this.orders = results;
    this.applicableOrderCount = count;
  }
}
</script>

<style lang="scss" scoped>
.caution {
  padding: 16px;
  margin-bottom: 40px;
  font-size: 14px;
  background-color: $primary-bg;
  border: 1px solid $yellow;
  border-radius: 4px;
}
</style>
